/**
 * Dashboard Header Component
 * Displays welcome message and organization context
 */

import { useOrganization } from '@/contexts/OrganizationContext'
import { useAuth } from '@/hooks/useAuth'
import { Badge } from '@/components/ui/badge'
import { Building2, Clock } from 'lucide-react'
import { useLocalization } from '@/contexts/LocalizationContext'
import { formatFullDate } from '@/utils/formatters'

interface DashboardHeaderProps {
  showGreeting?: boolean
  showLastUpdated?: boolean
}

export function DashboardHeader({
  showGreeting = true,
  showLastUpdated = false,
}: DashboardHeaderProps) {
  const { currentOrganization } = useOrganization()
  const { user } = useAuth()
  const { t, locale } = useLocalization()

  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return t('dashboard.goodMorning')
    if (hour < 17) return t('dashboard.goodAfternoon')
    return t('dashboard.goodEvening')
  }

  // Get user's first name (API uses snake_case: first_name)
  const firstName = user?.first_name || user?.phone_number?.slice(-4) || t('common.user')

  // Format current date
  const currentDate = formatFullDate(new Date(), locale)

  return (
    <div className="space-y-1">
      {/* Main Title */}
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-2xl lg:text-3xl font-bold tracking-tight">
          {showGreeting ? `${getGreeting()}, ${firstName}` : t('dashboard.title')}
        </h1>
        {currentOrganization && (
          <Badge 
            variant="secondary" 
            className="flex items-center gap-1.5 font-normal"
          >
            <Building2 className="h-3 w-3" />
            {currentOrganization.name}
          </Badge>
        )}
      </div>

      {/* Subtitle with date */}
      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <span>{currentDate}</span>
        {showLastUpdated && (
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {t('dashboard.lastUpdated')} {new Date().toLocaleTimeString()}
          </span>
        )}
      </div>
    </div>
  )
}

// ==================== Simple Header Variant ====================

export function SimpleDashboardHeader() {
  const { currentOrganization } = useOrganization()
  const { t } = useLocalization()

  return (
    <div className="flex items-center gap-3">
      <h1 className="text-2xl font-bold">{t('dashboard.title')}</h1>
      {currentOrganization && (
        <Badge variant="outline" className="font-normal">
          {currentOrganization.name}
        </Badge>
      )}
    </div>
  )
}
